import React, { useState, useEffect, useMemo } from "react";
import { applyLadderSummary } from "./swipe3/shared";
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine,
  PieChart, Pie, Cell,
} from "recharts";
import { API } from "../lib/config";

/* ============================================================
   OMEGA ZI — AGENT PORTFOLIO CARDS
   Real trading-floor roster and per-agent portfolio snapshot,
   pulled live from:
     GET /api/floor-chat/roster
     GET /api/floor-chat/agent/{agentKey}/portfolio
   No mock roster, no invented metrics. Every number rendered
   here traces to a real stored field or a real formula the
   backend already uses elsewhere (Sharpe/maxDrawdown/profitFactor
   match /api/performance/attribution's math; the USD unlock
   ladder matches capital_service.REWARD_LADDER; the Kelly figure
   uses engine.kelly_size(); the 12-vector Zi breakdown is the
   real OMEGA_WEIGHTS vector set averaged from this agent's own
   trades — direction_logit, iv_rank, greeks_alignment, etc.).

   Deliberately absent, because no real per-agent backend data
   exists for them: stock/dividend holdings, a three-way
   options/equity/crypto "engine" split, an allocation pie,
   per-agent strategy book-weights, and a Monte Carlo forecast
   cone. Nothing here fills those gaps with fabricated numbers.
   ============================================================ */

const ROOM_LABEL = {
  front_office: "Front Office",
  options: "Options Pit",
  yield: "Yield Desk",
  crypto: "Crypto Vault",
};
// Mapped onto the app's own themed palette (App.css :root / [data-theme="light"])
// rather than fixed hex, so this tab flips with the real Day/Night toggle
// like every other tab does — no separate always-dark skin.
const ROOM_COLOR = {
  front_office: "var(--muted)",
  options: "var(--purple)",
  yield: "var(--green)",
  crypto: "var(--yellow)",
};

const POS = "var(--green)", NEG = "var(--red)", MUT = "var(--muted)", INK = "var(--text)", GOLD = "var(--yellow)";
const sign = (n) => (n == null ? MUT : n >= 0 ? POS : NEG);
const pct = (n) => (n == null ? "—" : (n >= 0 ? "+" : "") + n.toFixed(2) + "%");
const usd0 = (n) => (n == null ? "—" : (n < 0 ? "-$" : "$") + Math.abs(Math.round(n)).toLocaleString("en-US"));
const usd2 = (n) => (n == null ? "—" : (n < 0 ? "-$" : "$") + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
// zBucks is paper currency, not real USD — "$" is reserved for genuinely
// real-money figures only: usdLedger's balance/delegated (profile.real_usd_
// balance/real_usd_delegated, literally real dollars, currently $0 for
// every agent since none has graduated) and the goal card's realUsdAtGoal
// (a real-dollar PROJECTION of what a zBucks goal would unlock, per the
// reward ladder — not currently-held money either, but a genuinely
// dollar-denominated figure). Found in review: totalPnl/todayPnl and the
// goal card's target/current were being formatted with usd0/usd2 despite
// being 100% zBucks-derived — agent_zbucks_service.py's settle_zbucks() is
// the ONLY code path that ever writes profile.total_pnl, so this field can
// never actually contain real-broker P&L, no matter how the account trades.
// It's the correct field to GATE the real-USD reward ladder on (prove
// paper profitability before real capital), but labeling it "$"/"(real)"
// falsely implied money that doesn't exist in the actual brokerage account.
// Every zBucks-denominated figure (paper balance, zBucks P/L, zBucks-kind
// positions and wire events, zBucks-sized Kelly, totalPnl/todayPnl, goal
// target/current) uses "zB" instead so the two currencies never look
// interchangeable.
const zb0 = (n) => (n == null ? "—" : (n < 0 ? "-zB" : "zB") + Math.abs(Math.round(n)).toLocaleString("en-US"));
const zb2 = (n) => (n == null ? "—" : (n < 0 ? "-zB" : "zB") + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const num2 = (n) => (n == null ? "—" : n.toFixed(2));
const pc0 = (n) => (n == null ? "—" : Math.round(n * 100) + "%");

const GOAL_STATUS_LABEL = {
  hit: "🎯 goal hit",
  ahead: "ahead of pace",
  on_pace: "on pace",
  behind: "behind pace",
  undetermined: "pace n/a",
};
const GOAL_STATUS_COLOR = {
  hit: "var(--yellow)",
  ahead: "var(--green)",
  on_pace: "var(--text)",
  behind: "var(--red)",
  undetermined: "var(--muted)",
};

const VECTOR_LABEL = {
  direction_logit: "Direction",
  iv_rank: "IV Rank",
  greeks_alignment: "Greeks Alignment",
  capital_efficiency: "Capital Efficiency",
  whale_flow: "Whale Flow",
  regime_hmm: "Regime Fit",
  tech_structure: "Tech Structure",
  liquidity: "Liquidity",
  news_sentiment: "News Sentiment",
  event_risk: "Event Risk",
  bayesian_prior: "Bayesian Prior",
  meta_confidence: "Meta Confidence",
};
// Real definitions + real firm-wide weights, straight from engine.py's
// OMEGA_WEIGHTS comments — not paraphrased into anything the backend
// doesn't actually mean.
const VECTOR_DESC = {
  direction_logit: { d: "Statistical price-direction probability for the trade thesis.", w: 0.14 },
  iv_rank: { d: "Implied-volatility percentile rank versus its own history.", w: 0.10 },
  greeks_alignment: { d: "How well the position's Greeks are optimized for the setup.", w: 0.09 },
  capital_efficiency: { d: "Expected value per dollar of buying power committed.", w: 0.06 },
  whale_flow: { d: "Institutional/large options flow support for the trade.", w: 0.09 },
  regime_hmm: { d: "Fitness of the setup against the detected HMM market regime.", w: 0.09 },
  tech_structure: { d: "Technical structure — support/resistance and trend context.", w: 0.07 },
  liquidity: { d: "Liquidity quality of the instrument (a hard veto floor below 0.40).", w: 0.08 },
  news_sentiment: { d: "News/NLP sentiment read on the underlying.", w: 0.06 },
  event_risk: { d: "Earnings/FDA/FOMC event risk inside the trade window.", w: 0.07 },
  bayesian_prior: { d: "Historical win probability for this setup type.", w: 0.07 },
  meta_confidence: { d: "Sub-model agreement — also carries independent veto authority.", w: 0.08 },
};

function useFetch(url, pollMs) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    const run = (isFirst) => {
      if (isFirst) setState((s) => ({ ...s, loading: true, error: null }));
      fetch(url, { credentials: "include" })
        .then(async (r) => {
          if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
          return r.json();
        })
        .then((data) => { if (!cancelled) setState({ data, loading: false, error: null }); })
        .catch((error) => { if (!cancelled) setState({ data: null, loading: false, error: String(error) }); });
    };
    run(true);
    // Cards used to fetch once on mount and never again — the headline
    // P&L looked frozen even after the backend started including live
    // mark-to-market, since nothing ever asked for a fresh number. 2s
    // matches the leaderboard's poll and the 5s server-side quote cache
    // underneath (cached_quotes ttl=5 in server.py) — going faster than
    // that buys no fresher data, just more requests.
    const id = pollMs ? setInterval(() => run(false), pollMs) : null;
    return () => { cancelled = true; if (id) clearInterval(id); };
  }, [url, pollMs]);
  return state;
}

function Row({ k, v, color }) {
  return (
    <div className="pc2-row">
      <span className="pc2-k">{k}</span>
      <span className="pc2-v" style={{ color: color || INK }}>{v}</span>
    </div>
  );
}

function LadderRow({ rung }) {
  return (
    <div className="pc2-ladder-row">
      <span className="pc2-ladder-l">Rung {rung.rung}</span>
      <div className="pc2-ladder-bar">
        <div className="pc2-ladder-fill" style={{ width: rung.progressPct + "%", background: rung.cleared ? POS : GOLD }} />
      </div>
      <span className="pc2-ladder-v" style={{ color: rung.cleared ? POS : INK }}>
        {usd0(rung.pnlThreshold)} P&amp;L → {usd0(rung.usdUnlocked)}
        {rung.cleared && <span className="pc2-ladder-done"> ✓</span>}
      </span>
    </div>
  );
}

// Real cumulative P&L, built from this agent's own recent floor_events —
// no simulated/projected path. Wire arrives newest-first from the backend;
// reversed here to plot chronologically, running a real running total
// (mixing kinds, same merged accounting totalPnl already uses) rather than
// a fabricated Monte Carlo cone.
function buildPnlHistory(wire) {
  const chronological = [...wire].reverse();
  let cum = 0;
  const points = chronological
    .filter((w) => w.action === "close" && w.pnl != null)
    .map((w, i) => {
      cum += w.pnl;
      return { i, cum, t: w.timestamp, ticker: w.ticker, kind: w.kind };
    });
  return points;
}

function PnlHistoryTip({ active, payload, accent }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const money = d.kind === "zbucks" ? zb0 : usd0;
  return (
    <div className="oz2-chart-tip">
      <span style={{ color: accent }}>{money(d.cum)}</span>
      <div className="oz2-chart-tip-sub">{d.ticker} · {d.t ? new Date(d.t).toLocaleDateString() : ""}</div>
    </div>
  );
}

function PnlHistoryChart({ wire, accent, agentId }) {
  const data = useMemo(() => buildPnlHistory(wire), [wire]);
  if (data.length < 2) {
    return <div className="oz2-empty">Not enough closed trades yet for a real P&amp;L history chart ({data.length} closed trade{data.length === 1 ? "" : "s"} so far).</div>;
  }
  return (
    <>
      <ResponsiveContainer width="100%" height={140}>
        <AreaChart data={data} margin={{ top: 6, right: 4, bottom: 0, left: 4 }}>
          <defs>
            <linearGradient id={`pnlh-${agentId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity={0.32} />
              <stop offset="100%" stopColor={accent} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="i" hide />
          <YAxis domain={["auto", "auto"]} hide />
          <ReferenceLine y={0} stroke="var(--border)" strokeDasharray="2 3" />
          <Tooltip content={<PnlHistoryTip accent={accent} />} cursor={{ stroke: accent, strokeOpacity: 0.3 }} />
          <Area type="monotone" dataKey="cum" stroke={accent} strokeWidth={2} fill={`url(#pnlh-${agentId})`} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
      <div className="oz2-curve-cap">{data.length} REAL CLOSED TRADES · RUNNING TOTAL, NOT A PROJECTION</div>
    </>
  );
}

function TradeMixDonut({ zbucksCount, realCount, accent }) {
  const total = zbucksCount + realCount;
  if (total === 0) return <div className="oz2-empty">No closed trades yet.</div>;
  const data = [
    { k: "zbucks", label: "zBucks", n: zbucksCount, c: GOLD },
    { k: "real", label: "Real", n: realCount, c: accent },
  ].filter((d) => d.n > 0);
  return (
    <div className="oz2-donut">
      <ResponsiveContainer width="52%" height={150}>
        <PieChart>
          <Pie data={data} dataKey="n" nameKey="label" innerRadius={36} outerRadius={58} paddingAngle={2} stroke="none">
            {data.map((d) => <Cell key={d.k} fill={d.c} />)}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="oz2-donut-legend">
        {data.map((d) => (
          <div className="oz2-donut-row" key={d.k}>
            <span className="oz2-swatch" style={{ background: d.c }} />
            <span className="oz2-donut-k">{d.label} trades</span>
            <span className="oz2-donut-v">{d.n}</span>
          </div>
        ))}
        <div className="oz2-donut-total">{total} closed total</div>
      </div>
    </div>
  );
}

function PopCard({ pop, p, accent, onClose, zbTotalValue, openPositionsValue }) {
  if (!pop) return null;
  let title = "", sub = "", body = null;

  if (pop.type === "zi") {
    const entries = Object.entries(p.ziScore.vectors)
      .map(([k, v]) => ({ k, v, label: VECTOR_LABEL[k] || k, ...(VECTOR_DESC[k] || { d: "", w: 0 }) }))
      .sort((a, b) => b.v - a.v);
    title = "Zi Score " + (p.ziScore.average ?? "—");
    sub = p.name + " · 12-vector composite · " + p.ziScore.tradesUsed + " trades";
    body = (
      <>
        <div className="pc2-read">
          {p.ziScore.tradesUsed
            ? `Averaged from ${p.name}'s own ${p.ziScore.tradesUsed} trade${p.ziScore.tradesUsed === 1 ? "" : "s"} that stored a vector snapshot at entry. Each vector is a real 0–100 signal weighted by the firm-wide OMEGA_WEIGHTS — the same weights every agent's trades are scored against.`
            : `${p.name} hasn't opened a trade with a stored Zi vector snapshot yet, so there's nothing real to average — this isn't defaulted to a fake number.`}
        </div>
        {entries.length > 0 && (
          <>
            <div className="pc2-sub-t">ALL TWELVE VECTORS</div>
            {entries.map((e) => (
              <div className="pc2-vec" key={e.k}>
                <div className="pc2-vec-head"><span>{e.label}</span><b>{e.v.toFixed(1)} <span className="pc2-vec-w">· {(e.w * 100).toFixed(0)}% weight</span></b></div>
                <div className="pc2-vec-bar"><div className="pc2-vec-fill" style={{ width: e.v + "%", background: accent }} /></div>
                <div className="pc2-vec-m">{e.d}</div>
              </div>
            ))}
          </>
        )}
      </>
    );
  } else if (pop.type === "ladder") {
    title = "USD Reward Ladder";
    sub = p.name + (p.usdLedger.unlocked ? " · unlocked" : " · locked");
    body = (
      <>
        <Row k="Real USD delegated" v={usd2(p.usdLedger.delegated)} color={GOLD} />
        <Row k="Real USD balance" v={usd2(p.usdLedger.balance)} color={GOLD} />
        <Row k="Current rung" v={p.usdLedger.rewardRung} />
        <div className="pc2-sub-t">RUNGS</div>
        <div className="oz2-ladder">{p.usdLedger.ladder.map((r) => <LadderRow key={r.rung} rung={r} />)}</div>
        <div className="pc2-read">Gated purely on {p.name}'s own zBucks all-time P&amp;L ({zb0(p.account.totalPnl)}) crossing each fixed threshold — there's no Zi-score or win-streak requirement layered on top; capital_service.py's REWARD_LADDER is the entire gate.</div>
      </>
    );
  } else if (pop.type === "position") {
    const h = pop.data;
    title = h.ticker;
    sub = (h.desk === "options" ? "Options" : "Crypto") + " · " + p.name;
    body = (
      <>
        {h.optionType && <Row k="Type" v={h.optionType.toUpperCase()} />}
        {h.strike != null && <Row k="Strike" v={h.strike} />}
        {h.expiry && <Row k="Expiry" v={h.expiry + (h.dte != null ? ` (${h.dte} DTE)` : "")} />}
        <Row k="Quantity" v={h.quantity} />
        <Row k="Entry price" v={h.entryPrice != null ? h.entryPrice.toLocaleString() : "—"} />
        <Row k="Current price" v={h.currentPrice != null ? h.currentPrice.toLocaleString() : "—"} />
        {h.currentStockPrice != null && <Row k="Underlying price" v={h.currentStockPrice.toLocaleString()} />}
        <Row k="P/L (zBucks)" v={h.pnl != null ? zb0(h.pnl) + " (" + pct(h.pnlPct) + ")" : "—"} color={sign(h.pnl)} />
        {h.ziScore != null && <Row k="Zi at entry" v={h.ziScore + (h.ziClass ? ` (${h.ziClass})` : "")} />}
        {h.profitTarget != null && <Row k="Profit target" v={h.profitTarget.toLocaleString()} color={POS} />}
        {h.stopLoss != null && <Row k="Stop loss" v={h.stopLoss.toLocaleString()} color={NEG} />}
        {h.openedAt && <Row k="Opened" v={new Date(h.openedAt).toLocaleString()} />}
        {h.liveGreeks && (
          <>
            <div className="pc2-sub-t">LIVE GREEKS (current price/DTE, entry IV)</div>
            <Row k="Delta" v={h.liveGreeks.delta.toFixed(4)} />
            <Row k="Gamma" v={h.liveGreeks.gamma.toFixed(5)} />
            <Row k="Theta" v={h.liveGreeks.theta.toFixed(4)} color={NEG} />
            <Row k="Vega" v={h.liveGreeks.vega.toFixed(4)} />
          </>
        )}
        {h.greeksAtEntry && (
          <>
            <div className="pc2-sub-t">GREEKS AT ENTRY</div>
            <Row k="Delta" v={Number(h.greeksAtEntry.delta ?? 0).toFixed(4)} />
            <Row k="IV" v={h.greeksAtEntry.iv != null ? pc0(h.greeksAtEntry.iv) : "—"} />
          </>
        )}
        {h.reason && <div className="pc2-read">{h.reason}</div>}
        <div className="pc2-read">Every open position here is a zBucks paper trade — the endpoint this reads from only ever returns zBucks-kind positions, never real-broker ones.{h.liveGreeks ? " Live Greeks are recomputed off the real current stock price and days-to-expiry, reusing entry IV since there's no fresh IV-surface lookup here." : ""}</div>
      </>
    );
  } else if (pop.type === "risk") {
    const rb = p.riskBudget;
    title = "Risk Budget";
    sub = p.name + " · " + rb.regime + " regime";
    body = (
      <>
        <Row k="Regime" v={rb.regime} />
        <Row k="Risk tier" v={rb.riskTier.replace("_", " ")} color={accent} />
        <Row k="Size multiplier" v={rb.sizeMult.toFixed(2) + "×"} />
        <Row k="Portfolio heat cap" v={pc0(rb.heatCapPct)} />
        <Row k="Single-trade cap" v={pc0(rb.singleTradeCapPct)} />
        <Row k="Current heat used" v={pc0(rb.currentHeatPct)} color={rb.currentHeatPct > rb.heatCapPct ? NEG : POS} />
        <Row k="Current heat ($)" v={zb0(rb.currentHeatDollars)} />
        <div className="pc2-read">Uses Jordan's real regime-scaled heat-cap constants ({rb.regime === "GREEN" ? "12%" : rb.regime === "RED" ? "5%" : "8%"} base, floored at 60% of the zBucks single-trade allowance) and {p.name}'s own real trust score — a blend of Wilson win-rate lower bound, confidence, and live win/loss streak — not a fixed number every agent shares. Current heat is summed from real open position risk against the real zBucks balance.</div>
      </>
    );
  } else if (pop.type === "performance") {
    const zk = p.performance.kelly;
    const rp = p.performanceReal;
    title = "Performance";
    sub = p.name + " · " + p.performance.totalTrades + " zBucks + " + rp.totalTrades + " real closed trades";
    body = (
      <>
        <div className="pc2-sub-t">ZBUCKS (PAPER)</div>
        {p.performance.totalTrades === 0 ? (
          <div className="pc2-read">No closed zBucks trades yet for {p.name}.</div>
        ) : (
          <>
            <Row k="Win rate" v={p.performance.winRate + "%"} color={p.performance.winRate >= 50 ? POS : NEG} />
            <Row k="Avg win" v={zb0(p.performance.avgWin)} color={POS} />
            <Row k="Avg loss" v={zb0(-p.performance.avgLoss)} color={NEG} />
            <Row k="Profit factor" v={p.performance.profitFactor != null ? p.performance.profitFactor.toFixed(2) : "not enough loss trades to compute"} />
            <Row k="Max drawdown" v={"-" + zb0(p.performance.maxDrawdown)} color={NEG} />
            <Row k="Sharpe" v={p.performance.sharpe != null ? p.performance.sharpe.toFixed(2) : "needs ≥4 closed trades"} />
            {zk ? (
              <>
                <Row k="Kelly fraction (¼-Kelly, clamped)" v={(zk.fraction * 100).toFixed(1) + "%"} color={accent} />
                <Row k="Full Kelly f*" v={(zk.fStar * 100).toFixed(1) + "%"} />
                <Row k="Suggested size" v={zb2(zk.dollars)} />
              </>
            ) : <Row k="Kelly" v="needs an avg win and avg loss" />}
          </>
        )}
        <div className="pc2-sub-t">REAL (BROKER)</div>
        {rp.totalTrades === 0 ? (
          <div className="pc2-read">No closed real trades yet for {p.name} — most trading here happens on the zBucks paper ledger first.</div>
        ) : (
          <>
            <Row k="Win rate" v={rp.winRate + "%"} color={rp.winRate >= 50 ? POS : NEG} />
            <Row k="Avg win" v={usd0(rp.avgWin)} color={POS} />
            <Row k="Avg loss" v={usd0(-rp.avgLoss)} color={NEG} />
            <Row k="Profit factor" v={rp.profitFactor != null ? rp.profitFactor.toFixed(2) : "not enough loss trades to compute"} />
            <Row k="Max drawdown" v={"-" + usd0(rp.maxDrawdown)} color={NEG} />
            <Row k="Sharpe" v={rp.sharpe != null ? rp.sharpe.toFixed(2) : "needs ≥4 closed trades"} />
          </>
        )}
        <div className="pc2-read">autopilot_outcomes mixes real-broker and zBucks-paper closes, so these are computed separately instead of blending real and paper dollars into one number. Kelly sizing uses the zBucks balance ({zb2(p.account.zbucksBalance)}) — the only per-agent account size that actually exists.</div>
      </>
    );
  } else if (pop.type === "wire") {
    const w = pop.data;
    const money = w.kind === "zbucks" ? zb0 : usd0;
    title = w.ticker || "Floor event";
    sub = p.name + (w.timestamp ? " · " + new Date(w.timestamp).toLocaleString() : "");
    body = (
      <>
        <Row k="Action" v={(w.action || "—").toUpperCase()} />
        <Row k="Ledger" v={w.kind === "zbucks" ? "zBucks (paper)" : "Real (broker)"} color={w.kind === "zbucks" ? GOLD : accent} />
        {w.pnl != null && <Row k="P/L" v={money(w.pnl) + (w.pnlPct != null ? " (" + pct(w.pnlPct) + ")" : "")} color={sign(w.pnl)} />}
        {w.won != null && <Row k="Result" v={w.won ? "WIN" : "LOSS"} color={w.won ? POS : NEG} />}
        <div className="pc2-read">{w.message}</div>
      </>
    );
  } else if (pop.type === "account") {
    title = "Account";
    sub = p.name;
    body = (
      <>
        <Row k="Cash balance" v={zb2(p.account.zbucksBalance)} />
        <Row k="Open positions value" v={zb2(openPositionsValue)} />
        <Row k="Total portfolio value" v={zb2(zbTotalValue)} />
        <Row k="zBucks P/L" v={zb0(p.account.zbucksPnl)} color={sign(p.account.zbucksPnl)} />
        <Row k="Today's P/L (zBucks)" v={zb0(p.account.todayPnl)} color={sign(p.account.todayPnl)} />
        <Row k="All-time P/L (zBucks — gates real unlock)" v={zb0(p.account.totalPnl)} color={sign(p.account.totalPnl)} />
        <Row k="Unrealized (open positions)" v={(p.account.unrealizedPnl >= 0 ? "+" : "") + zb0(p.account.unrealizedPnl)} color={sign(p.account.unrealizedPnl)} />
        <Row k="Live total (realized + unrealized)" v={zb0(p.account.livePnl ?? p.account.totalPnl)} color={sign(p.account.livePnl ?? p.account.totalPnl)} />
        <Row k="Wins / Losses" v={`${p.account.wins} / ${p.account.losses}`} />
        {p.account.vaultDebt > 0 && <Row k="Vault debt" v={zb2(p.account.vaultDebt)} color={NEG} />}
        <div className="pc2-read">zBucks is {p.name}'s paper ledger, tracked in its own unit (zB) — it isn't real money until the USD reward ladder unlocks it. Today's/all-time P&amp;L are that same zBucks track record (recomputed live from closed trades rather than a stale stored field) — this is the number that gates the real-USD unlock, not real dollars itself.{p.account.vaultDebt > 0 ? " Vault debt is skimmed off future wins (garnished) before the rest reaches the free balance, until it's repaid." : ""}</div>
      </>
    );
  } else if (pop.type === "goal") {
    const g = p.goal;
    title = "Goal Pace";
    sub = p.name + " · " + (GOAL_STATUS_LABEL[g.status] || g.status);
    body = (
      <>
        <Row k="Target (zBucks)" v={zb0(g.target)} />
        <Row k="Basis" v={g.basis === "edge_based" ? "edge-based (proven expectancy)" : "probationary (not enough proven edge yet)"} />
        {g.expectancyPerTrade != null && <Row k="Expectancy / trade (zBucks)" v={zb0(g.expectancyPerTrade)} color={sign(g.expectancyPerTrade)} />}
        <Row k="Current (zBucks)" v={zb0(g.current)} color={sign(g.current)} />
        <Row k="Elapsed today" v={pc0(g.elapsedFraction)} />
        <Row k="Pace" v={g.pacePct != null ? pc0(g.pacePct) + " of expected pace" : "n/a"} color={GOAL_STATUS_COLOR[g.status]} />
        <Row k="Status" v={(GOAL_STATUS_LABEL[g.status] || g.status).toUpperCase()} color={GOAL_STATUS_COLOR[g.status]} />
        {g.goldStar && <Row k="Gold star" v="🌟 goal hit — well earned" color={GOLD} />}
        {g.accountabilityNote && <Row k="Check-in" v={g.accountabilityNote} color={NEG} />}
        <Row k="Real USD at this goal" v={usd0(g.realUsdAtGoal)} color={GOLD} />
        {g.caveats.length > 0 && <div className="pc2-read">{g.caveats.join(" · ")}</div>}
        <div className="pc2-read">Goal is expectancy-based off {p.name}'s own zBucks win-rate/avg-win/avg-loss over the next ~10 trades (the same formula the firm's admin goal board uses) — a trader without a proven positive edge yet gets an honest, modest probationary goal instead of a fabricated stretch target. Pace compares zBucks P&amp;L so far against how far through today's clock we are.</div>
      </>
    );
  } else if (pop.type === "rank") {
    const r = p.rank;
    title = "Risk-Adjusted Rank";
    sub = p.name + " · " + r.totalAgents + " agents";
    body = (
      <>
        <Row k="Raw rank (sorted by P&amp;L)" v={"#" + (r.raw ?? "—")} />
        <Row k="Fair rank (risk-adjusted)" v={"#" + (r.fair ?? "—")} color={accent} />
        <Row k="Fair score" v={r.fairScore != null ? r.fairScore.toFixed(2) : "—"} />
        <Row k="Confidence" v={r.fairConfidence || "—"} />
        <Row k="Win-rate lower bound" v={r.winRateLowerBound != null ? pc0(r.winRateLowerBound) : "not enough closed trades"} />
        {r.raw != null && r.fair != null && r.raw !== r.fair && (
          <div className="pc2-read">
            {r.fair > r.raw
              ? `${p.name} ranks higher on raw P&L (#${r.raw}) than on the fair, risk-adjusted score (#${r.fair}) — a real dollar lead built on a small or volatile sample gets discounted rather than trusted at face value.`
              : `${p.name} ranks higher on the fair, risk-adjusted score (#${r.fair}) than on raw P&L (#${r.raw}) — a smaller or steadier track record that raw-dollar sorting would undersell.`}
          </div>
        )}
        <div className="pc2-read">Fair rank uses a Wilson lower-bound on win rate plus a sample-size-adjusted confidence score on average P&amp;L — the same math the real /floor-chat/agents/zbucks leaderboard ranks the whole floor by, not a separate number invented for this card.</div>
      </>
    );
  } else if (pop.type === "methodFit") {
    const mf = p.methodFit;
    const fmtPath = (s) => (
      s.status === "probationary" ? (
        <Row k="Status" v={`probationary — ${s.sample} closed trade${s.sample === 1 ? "" : "s"}, needs 5`} />
      ) : (
        <>
          <Row k="Win rate" v={s.winRate + "%"} color={s.winRate >= 50 ? POS : NEG} />
          <Row k="Avg win" v={zb0(s.avgWin)} color={POS} />
          <Row k="Avg loss" v={zb0(-s.avgLoss)} color={NEG} />
          <Row k="EV / $ risked" v={s.evPerDollar != null ? s.evPerDollar.toFixed(4) : "—"} color={sign(s.evPerDollar)} />
        </>
      )
    );
    title = "Method Fit — " + mf.ticker;
    sub = p.name + (mf.preferredPath ? " · favors " + mf.preferredPath : " · both paths still probationary");
    body = (
      <>
        <div className="pc2-sub-t">OPTIONS PATH (generic pipeline, {mf.ticker})</div>
        {fmtPath(mf.options)}
        <div className="pc2-sub-t">YIELD PATH (real dividend execution, {mf.ticker})</div>
        {fmtPath(mf.yield)}
        {mf.throttleYieldEntry && (
          <div className="pc2-read" style={{ color: NEG }}>Options path currently shows a higher real EV/$ on {mf.ticker} — the yield scanner is throttling its own next entry this cycle rather than opening one anyway.</div>
        )}
        <div className="pc2-read">Compares {p.name}'s two real, live-trading paths on {mf.ticker}: the generic options pipeline vs. the real yield-desk execution leg (whose EV includes real collected dividends, not just price P&amp;L). Either side under 5 closed trades stays probationary — no comparison is asserted from a tiny sample. This never gates the options pipeline; it only throttles the yield scanner's own next entry.</div>
      </>
    );
  }

  return (
    <div className="pc2-backdrop" onClick={onClose}>
      <div className="pc2-card" style={{ "--hue": accent }} onClick={(e) => e.stopPropagation()}>
        <div className="pc2-head">
          <div><div className="pc2-title">{title}</div><div className="pc2-sub">{sub}</div></div>
          <button className="pc2-close" onClick={onClose}>×</button>
        </div>
        <div className="pc2-body">{body}</div>
      </div>
    </div>
  );
}

function AgentCard({ agentKey, roster }) {
  const rosterEntry = roster[agentKey];
  const { data: p, loading, error } = useFetch(`${API}/floor-chat/agent/${agentKey}/portfolio`, 2000);
  const [pop, setPop] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [holdingsTab, setHoldingsTab] = useState("all");
  const open = (type, data) => setPop({ type, data });
  const closeExpanded = () => { setExpanded(false); setPop(null); };

  // Lock page scroll while expanded so the card underneath the blurred
  // backdrop can't shift/scroll — it only moves again once the user
  // explicitly closes it (click-off or the × button).
  useEffect(() => {
    if (!expanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [expanded]);

  const radar = useMemo(() => {
    if (!p) return [];
    return Object.entries(p.ziScore.vectors).map(([k, v]) => ({ name: VECTOR_LABEL[k] || k, v }));
  }, [p]);

  if (loading) return <div className="oz2-card oz2-loading">Loading {rosterEntry?.name || agentKey}'s real portfolio…</div>;
  if (error) return <div className="oz2-card oz2-loading">Couldn't load {rosterEntry?.name || agentKey}: {error}</div>;
  if (!p) return null;

  const accent = p.color || ROOM_COLOR[p.room] || "#8B7CF6";
  const hasTrades = p.performance.totalTrades > 0;
  // zbucksBalance is raw CASH — a position's cost is debited the instant it
  // opens, so money genuinely sitting in an open trade just disappeared from
  // this card's headline number until the trade closed. p.positions already
  // carries entryPrice/currentPrice/quantity/desk for every open position,
  // so the current market value is derivable with no extra fetch.
  const openPositionsValue = (p.positions || []).reduce((sum, pos) => {
    const mult = pos.desk === "options" ? 100 : 1;
    return sum + Number(pos.entryPrice || 0) * Number(pos.quantity || 0) * mult + Number(pos.pnl || 0);
  }, 0);
  const zbTotalValue = Number(p.account.zbucksBalance || 0) + openPositionsValue;

  // Collapsed grid tile — basic info only. Click anywhere to expand into the
  // full card as a blurred-backdrop overlay instead of always rendering
  // everything inline (23 full cards at once was a lot to scan).
  const summary = (
    <div className="oz2-card oz2-summary oz2-click" style={{ "--accent": accent, "--glow": accent + "26" }} onClick={() => setExpanded(true)}>
      <div className="oz2-head">
        <div className="oz2-id">
          <div className="oz2-avatar" style={{ borderColor: accent, color: accent }}>{p.initials}</div>
          <div>
            <div className="oz2-name">{p.name}</div>
            <div className="oz2-role">{p.role} · <span style={{ color: ROOM_COLOR[p.room] }}>{ROOM_LABEL[p.room] || p.room}</span></div>
          </div>
        </div>
        <div className="oz2-zi" style={{ borderColor: accent }}>
          <div className="oz2-zi-n" style={{ color: accent }}>{p.ziScore.average ?? "—"}</div>
          <div className="oz2-zi-l">{p.ziScore.ziClass || "ZI SCORE"}</div>
        </div>
      </div>
      <div className="oz2-summary-hero">
        <div className="oz2-hero-l">Total Portfolio Value</div>
        <div className="oz2-hero-v">{zb2(zbTotalValue)}</div>
        <div className="oz2-hero-sub">
          <span style={{ color: sign(p.account.livePnl ?? p.account.totalPnl) }}>{zb0(p.account.livePnl ?? p.account.totalPnl)} live P&amp;L (zBucks)</span>
        </div>
      </div>
      <div className="oz2-summary-strip">
        <span>{p.performance.winRate}% win rate</span>
        <span className="oz2-dot">·</span>
        <span>{p.positionCount} open</span>
        <span className="oz2-dot">·</span>
        <span style={{ color: p.usdLedger.unlocked ? GOLD : MUT }}>{p.usdLedger.unlocked ? "USD unlocked" : "rung " + p.usdLedger.rewardRung}</span>
      </div>
      <div className="oz2-summary-strip">
        <span style={{ color: GOAL_STATUS_COLOR[p.goal.status] || MUT }}>{GOAL_STATUS_LABEL[p.goal.status] || p.goal.status}</span>
        <span className="oz2-dot">·</span>
        <span>fair rank {p.rank.fair ?? "—"}/{p.rank.totalAgents}</span>
        {p.rank.raw != null && p.rank.fair != null && p.rank.raw !== p.rank.fair && (
          <span className="oz2-rank-diff">(raw {p.rank.raw})</span>
        )}
      </div>
      <div className="oz2-summary-cta">View full portfolio →</div>
    </div>
  );

  if (!expanded) return summary;

  return (
    <>
      {summary}
      <div className="oz2-expand-backdrop" onClick={closeExpanded}>
        <div className="oz2-card oz2-expand-card" style={{ "--accent": accent, "--glow": accent + "26" }} onClick={(e) => e.stopPropagation()}>
          <button className="oz2-expand-close" onClick={closeExpanded}>×</button>
          <div className="oz2-head">
        <div className="oz2-id">
          <div className="oz2-avatar" style={{ borderColor: accent, color: accent }}>{p.initials}</div>
          <div>
            <div className="oz2-name">{p.name}</div>
            <div className="oz2-role">{p.role} · <span style={{ color: ROOM_COLOR[p.room] }}>{ROOM_LABEL[p.room] || p.room}</span></div>
          </div>
        </div>
        <div className="oz2-zi oz2-click" style={{ borderColor: accent }} onClick={() => open("zi")}>
          <div className="oz2-zi-n" style={{ color: accent }}>{p.ziScore.average ?? "—"}</div>
          <div className="oz2-zi-l">{p.ziScore.ziClass ? p.ziScore.ziClass + " · " : ""}{p.ziScore.tradesUsed ? `${p.ziScore.tradesUsed} trades` : "no trades yet"}</div>
        </div>
      </div>

      {p.mandate && <div className="oz2-mandate">{p.mandate}</div>}

      <div className="oz2-hero oz2-click" onClick={() => open("account")}>
        <div>
          <div className="oz2-hero-l">Total Portfolio Value</div>
          <div className="oz2-hero-v">{zb2(zbTotalValue)}</div>
          <div className="oz2-hero-sub">
            <span style={{ color: sign(p.account.todayPnl) }}>{zb0(p.account.todayPnl)} today (zBucks)</span>
            <span className="oz2-dot">·</span>
            <span style={{ color: sign(p.account.livePnl ?? p.account.totalPnl) }}>{zb0(p.account.livePnl ?? p.account.totalPnl)} live P&amp;L (zBucks)</span>
          </div>
          {Math.abs(p.account.unrealizedPnl || 0) > 0.01 && (
            <div className="oz2-hero-sub" style={{ marginTop: 2 }}>
              <span style={{ color: MUT, fontSize: 11 }}>
                {zb0(p.account.totalPnl)} realized · <span style={{ color: sign(p.account.unrealizedPnl) }}>{p.account.unrealizedPnl >= 0 ? "+" : ""}{zb0(p.account.unrealizedPnl)} unrealized</span>
              </span>
            </div>
          )}
          {openPositionsValue > 0.01 && (
            <div className="oz2-hero-sub" style={{ marginTop: 2 }}>
              <span style={{ color: MUT, fontSize: 11 }}>{zb2(p.account.zbucksBalance)} cash + {zb2(openPositionsValue)} in open positions</span>
            </div>
          )}
        </div>
      </div>

      {/* Real personalized goal + pace — build_goal_board()'s own formula,
          run directly off this agent's own zBucks track record */}
      <div className="oz2-treasury oz2-click" onClick={() => open("goal")}>
        <div className="oz2-tr-head">
          <span className="oz2-tr-title">GOAL PACE</span>
          <span className="oz2-tr-badge" style={{ color: GOAL_STATUS_COLOR[p.goal.status] || MUT, borderColor: GOAL_STATUS_COLOR[p.goal.status] || "var(--border)" }}>
            {(GOAL_STATUS_LABEL[p.goal.status] || p.goal.status).toUpperCase()}
          </span>
        </div>
        <Row k="Target" v={usd0(p.goal.target) + (p.goal.basis === "probationary" ? " (probationary)" : "")} />
        <Row k="Current" v={usd0(p.goal.current)} color={sign(p.goal.current)} />
        <div className="oz2-goal-bar"><div className="oz2-goal-fill" style={{ width: Math.max(0, Math.min(100, (p.goal.pacePct ?? 0) * 100)) + "%", background: GOAL_STATUS_COLOR[p.goal.status] || MUT }} /></div>
      </div>

      {/* Real risk-adjusted rank — Wilson lower-bound + sample-size-adjusted
          fair_score, the same math /floor-chat/agents/zbucks ranks by */}
      <div className="oz2-treasury oz2-click" onClick={() => open("rank")}>
        <div className="oz2-tr-head">
          <span className="oz2-tr-title">RISK-ADJUSTED RANK</span>
          <span className="oz2-tr-badge" style={{ color: accent, borderColor: accent }}>FAIR #{p.rank.fair ?? "—"} / {p.rank.totalAgents}</span>
        </div>
        <Row k="Raw rank (by P&amp;L)" v={"#" + (p.rank.raw ?? "—")} />
        <Row k="Fair rank (risk-adjusted)" v={"#" + (p.rank.fair ?? "—")} color={accent} />
        <Row k="Confidence" v={p.rank.fairConfidence || "—"} />
      </div>

      {/* Real USD reward ladder — gated purely on real total P&L, capital_service.REWARD_LADDER */}
      <div className="oz2-treasury oz2-click" onClick={() => open("ladder")}>
        <div className="oz2-tr-head">
          <span className="oz2-tr-title">USD REWARD LADDER</span>
          <span className={"oz2-tr-badge " + (p.usdLedger.unlocked ? "unlocked" : "locked")}>
            {p.usdLedger.unlocked ? "✓ UNLOCKED" : "🔒 RUNG " + p.usdLedger.rewardRung}
          </span>
        </div>
        <Row k="Real USD delegated" v={usd2(p.usdLedger.delegated)} color={GOLD} />
        <Row k="Real USD balance" v={usd2(p.usdLedger.balance)} color={GOLD} />
        <div className="oz2-ladder">
          {p.usdLedger.ladder.map((r) => <LadderRow key={r.rung} rung={r} />)}
        </div>
      </div>

      {/* Real cumulative P&L — running total from this agent's own closed
          floor-wire trades, not a simulated/projected path */}
      <div className="oz2-section-t">P&amp;L HISTORY · REAL CLOSED TRADES</div>
      <div className="oz2-pane">
        <PnlHistoryChart wire={p.wire} accent={accent} agentId={p.agentKey} />
      </div>

      <div className="oz2-mid">
        {/* Real 12-vector Omega breakdown, averaged from this agent's own trades */}
        {radar.length > 0 && (
          <div className="oz2-pane oz2-click" onClick={() => open("zi")}>
            <div className="oz2-pane-t">12-VECTOR ZI SCORE — averaged across this agent's own trades</div>
            <ResponsiveContainer width="100%" height={168}>
              <RadarChart data={radar} outerRadius="72%">
                <PolarGrid stroke="var(--border)" />
                <PolarAngleAxis dataKey="name" tick={{ fill: MUT, fontSize: 8 }} />
                <Radar dataKey="v" stroke={accent} fill={accent} fillOpacity={0.28} strokeWidth={1.5} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        )}
        {/* Real zBucks-vs-real trade mix — not a fake asset allocation, an
            honest split of how many of this agent's closed trades were paper
            vs broker */}
        <div className="oz2-pane oz2-click" onClick={() => open("performance")}>
          <div className="oz2-pane-t">TRADE MIX</div>
          <TradeMixDonut zbucksCount={p.performance.totalTrades} realCount={p.performanceReal.totalTrades} accent={accent} />
        </div>
      </div>

      <div className="oz2-metrics oz2-click" onClick={() => open("performance")}>
        <div className="oz2-stat"><div className="oz2-stat-l">WIN RATE</div><div className="oz2-stat-v">{p.performance.winRate}%</div></div>
        <div className="oz2-stat"><div className="oz2-stat-l">TRADES</div><div className="oz2-stat-v">{p.performance.totalTrades}</div></div>
        <div className="oz2-stat"><div className="oz2-stat-l">SHARPE</div><div className="oz2-stat-v">{num2(p.performance.sharpe)}</div></div>
        <div className="oz2-stat"><div className="oz2-stat-l">MAX DD</div><div className="oz2-stat-v" style={{ color: p.performance.maxDrawdown ? NEG : INK }}>{p.performance.maxDrawdown ? "-" + zb0(p.performance.maxDrawdown) : "—"}</div></div>
        <div className="oz2-stat"><div className="oz2-stat-l">KELLY</div><div className="oz2-stat-v" style={{ color: accent }}>{p.performance.kelly ? (p.performance.kelly.fraction * 100).toFixed(1) + "%" : "—"}</div></div>
      </div>
      {!hasTrades && <div className="oz2-empty-note">No closed trades yet for {p.name} — performance metrics will populate as the first ones close.</div>}

      {/* Real risk budget — Jordan's own regime-scaled heat-cap math, applied
          to this agent's real trust score and real current open-position risk */}
      <div className="oz2-treasury oz2-click" onClick={() => open("risk")}>
        <div className="oz2-tr-head">
          <span className="oz2-tr-title">RISK BUDGET · {p.riskBudget.regime} REGIME</span>
          <span className="oz2-tr-badge" style={{ color: accent, borderColor: accent }}>{p.riskBudget.riskTier.replace("_", " ").toUpperCase()}</span>
        </div>
        <Row k="Heat used" v={pc0(p.riskBudget.currentHeatPct) + " of " + pc0(p.riskBudget.heatCapPct) + " cap"} color={p.riskBudget.currentHeatPct > p.riskBudget.heatCapPct ? NEG : POS} />
        <div className="oz2-goal-bar"><div className="oz2-goal-fill" style={{ width: Math.max(0, Math.min(100, (p.riskBudget.heatCapPct ? p.riskBudget.currentHeatPct / p.riskBudget.heatCapPct : 0) * 100)) + "%", background: p.riskBudget.currentHeatPct > p.riskBudget.heatCapPct ? NEG : accent }} /></div>
      </div>

      {/* This agent's own earned zBucks tier — judged purely on their own
          balance (GET /floor-chat/agent/{key}/portfolio's account.zbucksTier),
          completely independent of the shared real-money account's
          recovery/kill-switch state shown elsewhere in the app. */}
      {p.account?.zbucksTier && (() => {
        const tierMeta = {
          freedom:  { label: "FREEDOM TRADING", color: POS, desc: "Earned a looser gate + larger size on their own zBucks book" },
          standard: { label: "STANDARD", color: accent, desc: "Today's baseline zBucks gate/sizing" },
          defense:  { label: "DEFENSE MODE", color: NEG, desc: "Tightened gate + smaller size — their own zBucks book only, not the real account" },
        }[p.account.zbucksTier] || { label: p.account.zbucksTier.toUpperCase(), color: accent, desc: "" };
        return (
          <div className="oz2-treasury">
            <div className="oz2-tr-head">
              <span className="oz2-tr-title">ZBUCKS TIER</span>
              <span className="oz2-tr-badge" style={{ color: tierMeta.color, borderColor: tierMeta.color }}>{tierMeta.label}</span>
            </div>
            <div className="oz2-empty-note">{tierMeta.desc}</div>
          </div>
        );
      })()}

      {/* Real options-vs-yield method fit — only exists for the 5 yield-desk
          agents (Sterling/Maple/Birch/Cedar/Quill), once yield_zbucks_scanner.py
          gave them a second real, live-trading path to compare */}
      {p.methodFit && (
        <div className="oz2-treasury oz2-click" onClick={() => open("methodFit")}>
          <div className="oz2-tr-head">
            <span className="oz2-tr-title">METHOD FIT · {p.methodFit.ticker}</span>
            <span className="oz2-tr-badge" style={{ color: accent, borderColor: accent }}>{p.methodFit.preferredPath ? p.methodFit.preferredPath.toUpperCase() : "PROBATIONARY"}</span>
          </div>
          <Row k="Options EV/$" v={p.methodFit.options.evPerDollar != null ? p.methodFit.options.evPerDollar.toFixed(4) : "probationary"} color={sign(p.methodFit.options.evPerDollar)} />
          <Row k="Yield EV/$" v={p.methodFit.yield.evPerDollar != null ? p.methodFit.yield.evPerDollar.toFixed(4) : "probationary"} color={sign(p.methodFit.yield.evPerDollar)} />
        </div>
      )}

      {/* Real open holdings — options + crypto, live-repriced, exactly as
          /floor-chat/zbucks/positions serves them (zBucks paper only) */}
      <div className="oz2-section-t">HOLDINGS · {p.positionCount} live <span className="oz2-hint">tap for detail</span></div>
      <div className="oz2-tabs">
        {[
          { k: "all", label: "All", n: p.positions.length },
          { k: "options", label: "Options", n: p.positions.filter((h) => h.desk === "options").length },
          { k: "crypto", label: "Crypto", n: p.positions.filter((h) => h.desk === "crypto").length },
          { k: "yield", label: "Yield", n: p.positions.filter((h) => h.desk === "yield").length },
        ].map((t) => (
          <button key={t.k} className={"oz2-tab" + (holdingsTab === t.k ? " on" : "")} style={holdingsTab === t.k ? { color: accent, borderColor: accent } : {}} onClick={() => setHoldingsTab(t.k)}>
            {t.label} <span className="oz2-tab-n">{t.n}</span>
          </button>
        ))}
      </div>
      {(() => {
        const rows = holdingsTab === "all" ? p.positions : p.positions.filter((h) => h.desk === holdingsTab);
        if (!rows.length) return <div className="oz2-empty">No {holdingsTab === "all" ? "open positions" : holdingsTab + " positions"} right now.</div>;
        const showGreeks = holdingsTab === "options";
        const showDiv = holdingsTab === "yield";
        return (
          <div className="oz2-table-wrap">
            <table className="oz2-table">
              <thead><tr><th>TICKER</th><th>DESK</th><th>QTY</th><th>ENTRY</th><th>LAST</th>{showGreeks && <th>Δ / Θ (live)</th>}{showDiv && <th>DIV COLLECTED</th>}<th>P/L</th></tr></thead>
              <tbody>
                {rows.map((h, i) => (
                  <tr key={i} className="oz2-click" onClick={() => open("position", h)}>
                    <td className="sym">{h.ticker}{h.optionType && <span className="oz2-badge">{h.optionType?.toUpperCase()}</span>}</td>
                    <td>{h.desk}</td>
                    <td>{h.quantity}</td>
                    <td>{h.entryPrice != null ? h.entryPrice.toLocaleString() : "—"}</td>
                    <td>{h.currentPrice != null ? h.currentPrice.toLocaleString() : "—"}</td>
                    {showGreeks && <td>{h.liveGreeks ? `${h.liveGreeks.delta.toFixed(2)} / ${h.liveGreeks.theta.toFixed(2)}` : "—"}</td>}
                    {showDiv && <td style={{ color: h.totalDividendsCollected ? POS : MUT }}>{h.totalDividendsCollected ? zb0(h.totalDividendsCollected) : "—"}</td>}
                    <td style={{ color: sign(h.pnl) }}>{h.pnl != null ? zb0(h.pnl) + " (" + pct(h.pnlPct) + ")" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Real recent action feed — open/close vocabulary, from floor_events */}
      <div className="oz2-section-t">FLOOR WIRE · RECENT <span className="oz2-hint">tap for detail</span></div>
      {p.wire.length ? (
        <div className="oz2-wire">
          {p.wire.map((w, i) => (
            <div className="oz2-wire-row oz2-click" key={i} onClick={() => open("wire", w)}>
              <span className="oz2-wire-t">{w.timestamp ? new Date(w.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</span>
              <span className="oz2-wire-a" style={{ color: accent, borderColor: accent }}>{(w.action || "").toUpperCase() || "—"}</span>
              <span className="oz2-wire-d">{w.message}</span>
              <span className="oz2-wire-k" style={{ color: w.kind === "zbucks" ? GOLD : accent }}>{w.kind === "zbucks" ? "zB" : "$"}</span>
            </div>
          ))}
        </div>
      ) : <div className="oz2-empty">No recent floor activity for this agent.</div>}

          <PopCard pop={pop} p={p} accent={accent} onClose={() => setPop(null)} zbTotalValue={zbTotalValue} openPositionsValue={openPositionsValue} />
        </div>
      </div>
    </>
  );
}

const MEDAL = ["🥇", "🥈", "🥉"];

function TopThreePodium({ leaders, jumpTo }) {
  if (!leaders.length) return null;
  return (
    <section className="oz2-podium">
      <div className="oz2-podium-head">★ TOP 3 THIS WEEK — everyone on the floor is trading to take one of these spots</div>
      <div className="oz2-podium-row">
        {leaders.map((a, i) => (
          <button key={a.agent_key} className={"oz2-podium-slot oz2-podium-" + (i + 1)} onClick={() => jumpTo(a.agent_key)}>
            <div className="oz2-podium-medal">{MEDAL[i]}</div>
            <div className="oz2-podium-name" style={{ color: a.color }}>{a.name}</div>
            <div className="oz2-podium-pnl" style={{ color: sign(a.total_pnl) }}>{zb2(a.total_pnl)}</div>
            <div className="oz2-podium-sub">FAIR #{a.fair_rank} · {a.trades} trade{a.trades === 1 ? "" : "s"}</div>
          </button>
        ))}
      </div>
    </section>
  );
}

export default function OmegaZiPortfolioCards() {
  const [roster, setRoster] = useState(null);
  const [rosterError, setRosterError] = useState(null);
  // Single lightweight call for real fair_rank/total_pnl across the whole
  // floor — the same leaderboard math /floor-chat/agents/zbucks already
  // computes — reused here to rank-sort the roster and drive the top-3
  // podium instead of every card fetching its own rank redundantly.
  const { data: zbData } = useFetch(`${API}/floor-chat/agents/zbucks`, 5000);
  useEffect(() => { applyLadderSummary(zbData?.summary); }, [zbData]);

  useEffect(() => {
    fetch(`${API}/floor-chat/roster`, { credentials: "include" })
      .then(async (r) => { if (!r.ok) throw new Error(`${r.status} ${r.statusText}`); return r.json(); })
      .then((data) => setRoster(data.agents || {}))
      .catch((e) => setRosterError(String(e)));
  }, []);

  const rankByKey = useMemo(() => {
    const map = {};
    for (const row of (zbData?.leaderboard || [])) map[row.agent_key] = row;
    return map;
  }, [zbData]);

  // Flat, unsectioned roster — the room label is real (each agent does lean
  // toward one desk) but per the floor's own "no silos" doctrine it's origin
  // flavor on the card itself, not a fence the roster gets sorted into.
  // Ranked by real fair_rank (missing/unranked agents sort to the end) —
  // the floor is a competition for the top 3 spots, not an alphabetical list.
  const agents = useMemo(() => {
    if (!roster) return [];
    return Object.entries(roster)
      .filter(([, a]) => a.room !== "front_office")
      .map(([key, a]) => ({ key, ...a, fairRank: rankByKey[key]?.fair_rank ?? 999 }))
      .sort((a, b) => a.fairRank - b.fairRank || a.name.localeCompare(b.name));
  }, [roster, rankByKey]);

  const leaders = useMemo(() => {
    return (zbData?.leaderboard || [])
      .slice()
      .sort((a, b) => (a.fair_rank ?? 999) - (b.fair_rank ?? 999))
      .slice(0, 3)
      .map((row) => ({ ...row, color: roster?.[row.agent_key]?.color || GOLD }));
  }, [zbData, roster]);

  const jumpTo = (key) => document.getElementById("oz2-agent-" + key)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="oz2-root">
      <style>{CSS}</style>
      <header className="oz2-topbar">
        <div>
          <div className="oz2-brand">OMEGA <span>ZI</span></div>
          <div className="oz2-sub">REAL TRADING FLOOR ROSTER · live from /api/floor-chat · every trader has their own card below</div>
        </div>
      </header>

      {rosterError && <div className="oz2-error">Couldn't load the roster: {rosterError}</div>}

      <TopThreePodium leaders={leaders} jumpTo={jumpTo} />

      {/* Quick-jump index — one flat floor, no per-desk sections. Every
          trader gets their own full card further down the page, now ranked
          by real performance instead of alphabetically. */}
      {agents.length > 0 && (
        <nav className="oz2-roster">
          <div className="oz2-chips">
            {agents.map((a) => (
              <button key={a.key} className="oz2-chip" onClick={() => jumpTo(a.key)}>
                <span className="oz2-chip-dot" style={{ background: a.color }} />
                {a.name}
              </button>
            ))}
          </div>
        </nav>
      )}

      <main className="oz2-stage">
        <div className="oz2-card-stack">
          {agents.map((a) => (
            <div id={"oz2-agent-" + a.key} key={a.key} className="oz2-anchor">
              <AgentCard agentKey={a.key} roster={roster} />
            </div>
          ))}
        </div>
      </main>

      <footer className="oz2-foot">Every number above is either a stored field or a formula reused from an existing endpoint — nothing here is invented.</footer>
    </div>
  );
}

/* Themed entirely off the app's own CSS variables (App.css :root /
   [data-theme="light"]) — this tab flips with the real Day/Night toggle
   like every other tab, instead of carrying its own fixed dark skin. */
const CSS = `
.oz2-root{background:var(--bg);min-height:100%;color:var(--text);padding:20px 16px 40px;font-family:'Outfit',system-ui,sans-serif;}
.oz2-root *{box-sizing:border-box;}
.oz2-topbar{max-width:1400px;margin:0 auto 16px;}
.oz2-brand{font-size:24px;font-weight:700;letter-spacing:2px;}.oz2-brand span{color:var(--yellow);}
.oz2-sub{font-size:10px;color:var(--muted);letter-spacing:1px;margin-top:3px;font-family:'JetBrains Mono',monospace;}
.oz2-error{max-width:1400px;margin:0 auto 12px;color:var(--red);font-size:12px;}
.oz2-podium{max-width:1400px;margin:0 auto 18px;}
.oz2-podium-head{font-family:'JetBrains Mono',monospace;font-size:11px;letter-spacing:1.5px;color:var(--yellow);margin-bottom:10px;}
.oz2-podium-row{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;}
@media(max-width:760px){.oz2-podium-row{grid-template-columns:1fr;}}
.oz2-podium-slot{background:var(--surface);border:1px solid var(--border);border-radius:16px;padding:16px;text-align:left;cursor:pointer;transition:border-color .15s,transform .1s;}
.oz2-podium-slot:hover{border-color:var(--accent);transform:translateY(-2px);}
.oz2-podium-1{border-top:3px solid var(--yellow);}
.oz2-podium-2{border-top:3px solid var(--muted);}
.oz2-podium-3{border-top:3px solid #b87333;}
.oz2-podium-medal{font-size:22px;}
.oz2-podium-name{font-size:16px;font-weight:700;margin-top:4px;}
.oz2-podium-pnl{font-family:'JetBrains Mono',monospace;font-size:15px;font-weight:700;margin-top:2px;}
.oz2-podium-sub{font-family:'JetBrains Mono',monospace;font-size:9.5px;color:var(--muted);margin-top:4px;}
.oz2-roster{max-width:1400px;margin:0 auto 10px;}
.oz2-room-label{font-size:9px;letter-spacing:1.5px;font-family:'JetBrains Mono',monospace;margin-bottom:6px;}
.oz2-chips{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:10px;}
.oz2-chip{display:flex;align-items:center;gap:6px;background:var(--layer1);border:1px solid var(--border);color:var(--muted);font-family:'JetBrains Mono',monospace;font-size:11px;padding:6px 10px;border-radius:10px;cursor:pointer;}
.oz2-chip:hover{color:var(--text);background:var(--hover);}
.oz2-chip-dot{width:7px;height:7px;border-radius:3px;}
.oz2-stage{max-width:1400px;margin:0 auto;}
.oz2-room-head{font-family:'JetBrains Mono',monospace;font-size:13px;font-weight:700;letter-spacing:1.5px;margin:28px 0 12px;padding-bottom:8px;border-bottom:1px solid var(--border);}
.oz2-stage>section:first-child .oz2-room-head{margin-top:6px;}
.oz2-card-stack{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:18px;align-items:start;}
@media(max-width:1180px){.oz2-card-stack{grid-template-columns:repeat(2,minmax(0,1fr));}}
@media(max-width:760px){.oz2-card-stack{grid-template-columns:1fr;}}
.oz2-anchor{scroll-margin-top:16px;}
.oz2-card{background:var(--surface);border:1px solid var(--border);border-radius:20px;padding:22px;position:relative;overflow:hidden;}
.oz2-summary{display:flex;flex-direction:column;gap:2px;transition:border-color .15s;}
.oz2-summary:hover{border-color:var(--accent);box-shadow:0 0 0 1px var(--accent) inset;}
.oz2-summary-hero{margin-top:12px;}
.oz2-summary-strip{font-family:'JetBrains Mono',monospace;font-size:10.5px;color:var(--muted);margin-top:10px;display:flex;gap:6px;flex-wrap:wrap;}
.oz2-summary-cta{font-family:'JetBrains Mono',monospace;font-size:9.5px;color:var(--accent);margin-top:12px;letter-spacing:.5px;}
.oz2-rank-diff{color:var(--muted);}
.oz2-expand-backdrop{position:fixed;inset:0;background:rgba(0,0,0,0.55);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);display:flex;align-items:flex-start;justify-content:center;padding:32px 16px;z-index:150;overflow-y:auto;}
.oz2-expand-card{width:100%;max-width:640px;box-shadow:0 30px 90px rgba(0,0,0,0.45);animation:oz2Fade .12s linear;}
@keyframes oz2Fade{from{opacity:0;}to{opacity:1;}}
.oz2-expand-close{position:absolute;top:16px;right:16px;background:var(--layer2);border:1px solid var(--border);color:var(--text);width:32px;height:32px;border-radius:9px;font-size:19px;line-height:1;cursor:pointer;z-index:2;}
.oz2-expand-close:hover{background:var(--layer3);}
.oz2-loading{color:var(--muted);font-family:'JetBrains Mono',monospace;font-size:12px;padding:40px;text-align:center;}
.oz2-head{display:flex;justify-content:space-between;align-items:flex-start;}
.oz2-id{display:flex;gap:12px;align-items:center;}
.oz2-avatar{width:44px;height:44px;border-radius:12px;border:1.5px solid;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:15px;}
.oz2-name{font-size:19px;font-weight:700;}
.oz2-role{font-size:12px;color:var(--muted);margin-top:3px;}
.oz2-zi{text-align:center;border:1.5px solid;border-radius:14px;padding:7px 12px;min-width:100px;}
.oz2-zi-n{font-size:22px;font-weight:700;}
.oz2-zi-l{font-family:'JetBrains Mono',monospace;font-size:8px;color:var(--muted);margin-top:3px;}
.oz2-mandate{font-size:12px;color:var(--muted);margin:14px 0 4px;line-height:1.5;}
.oz2-hero{margin:14px 0 6px;}
.oz2-hero-l{font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--muted);}
.oz2-hero-v{font-size:36px;font-weight:700;margin-top:2px;}
.oz2-hero-sub{font-family:'JetBrains Mono',monospace;font-size:12px;margin-top:6px;display:flex;gap:8px;}
.oz2-dot{color:var(--muted);}
.oz2-treasury{margin-top:16px;border:1px solid var(--yellow-bg);border-radius:16px;padding:15px;background:var(--yellow-bg);}
.oz2-tr-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;}
.oz2-tr-title{font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:1.5px;}
.oz2-tr-badge{font-family:'JetBrains Mono',monospace;font-size:9px;font-weight:600;padding:2px 8px;border-radius:6px;border:1px solid;}
.oz2-tr-badge.unlocked{color:var(--yellow);border-color:var(--yellow-bg);}
.oz2-tr-badge.locked{color:var(--muted);border-color:var(--border);}
.pc2-row{display:flex;justify-content:space-between;padding:5px 0;font-family:'JetBrains Mono',monospace;font-size:12px;}
.pc2-k{color:var(--muted);}.pc2-v{font-weight:600;}
.oz2-ladder{display:flex;flex-direction:column;gap:9px;margin-top:10px;}
.oz2-goal-bar{height:6px;background:var(--layer3);border-radius:99px;overflow:hidden;margin-top:8px;}
.oz2-goal-fill{height:100%;border-radius:99px;}
.pc2-ladder-row{display:grid;grid-template-columns:56px 1fr auto;align-items:center;gap:10px;}
.pc2-ladder-l{font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--muted);}
.pc2-ladder-bar{height:6px;background:var(--layer3);border-radius:99px;overflow:hidden;}
.pc2-ladder-fill{height:100%;border-radius:99px;}
.pc2-ladder-v{font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--muted);}
.pc2-ladder-done{color:var(--green);}
.oz2-pane{background:var(--layer1);border:1px solid var(--border);border-radius:14px;padding:12px;margin-top:16px;}
.oz2-pane-t{font-family:'JetBrains Mono',monospace;font-size:9px;color:var(--muted);letter-spacing:1px;margin-bottom:4px;}
.oz2-mid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:16px;}
.oz2-mid .oz2-pane{margin-top:0;}
.oz2-curve-cap{font-family:'JetBrains Mono',monospace;font-size:8px;color:var(--muted);letter-spacing:1px;text-align:right;margin-top:2px;}
.oz2-chart-tip{background:var(--surface);border:1px solid var(--border);border-radius:8px;padding:6px 10px;font-family:'JetBrains Mono',monospace;font-size:12px;font-weight:600;box-shadow:0 8px 24px rgba(0,0,0,0.25);}
.oz2-chart-tip-sub{font-size:9px;color:var(--muted);font-weight:400;margin-top:2px;}
.oz2-donut{display:flex;align-items:center;}
.oz2-donut-legend{flex:1;display:flex;flex-direction:column;gap:6px;padding-left:4px;}
.oz2-donut-row{display:flex;align-items:center;gap:7px;font-family:'JetBrains Mono',monospace;font-size:10.5px;}
.oz2-swatch{width:9px;height:9px;border-radius:3px;flex:none;}
.oz2-donut-k{flex:1;color:var(--muted);}
.oz2-donut-v{color:var(--text);font-weight:600;}
.oz2-donut-total{font-family:'JetBrains Mono',monospace;font-size:9px;color:var(--muted);margin-top:4px;padding-top:6px;border-top:1px solid var(--divider);}
@media(max-width:520px){.oz2-mid{grid-template-columns:1fr;}}
.oz2-metrics{display:grid;grid-template-columns:repeat(auto-fit,minmax(72px,1fr));gap:8px;margin-top:14px;}
.oz2-stat{background:var(--layer1);border:1px solid var(--border);border-radius:11px;padding:9px 8px;text-align:center;}
.oz2-stat-l{font-family:'JetBrains Mono',monospace;font-size:8px;color:var(--muted);}
.oz2-stat-v{font-family:'JetBrains Mono',monospace;font-size:14px;font-weight:600;margin-top:3px;}
.oz2-empty-note{font-size:11px;color:var(--muted);margin-top:8px;font-style:italic;}
.oz2-section-t{font-family:'JetBrains Mono',monospace;font-size:9px;color:var(--muted);letter-spacing:1.5px;margin:20px 0 10px;}
.oz2-tabs{display:flex;gap:6px;margin-top:10px;border-bottom:1px solid var(--border);}
.oz2-tab{background:none;border:none;border-bottom:2px solid transparent;color:var(--muted);font-family:'JetBrains Mono',monospace;font-size:11px;font-weight:500;padding:7px 9px;cursor:pointer;letter-spacing:.5px;margin-bottom:-1px;}
.oz2-tab:hover{color:var(--text);}
.oz2-tab-n{opacity:.55;font-size:9px;}
.oz2-table-wrap{overflow-x:auto;margin-top:10px;}
.oz2-table{width:100%;border-collapse:collapse;font-family:'JetBrains Mono',monospace;font-size:11.5px;}
.oz2-table th{text-align:right;color:var(--muted);font-size:8.5px;padding:0 0 8px;border-bottom:1px solid var(--border);}
.oz2-table th:first-child{text-align:left;}
.oz2-table td{text-align:right;padding:8px 0;border-bottom:1px solid var(--divider);}
.oz2-table td:first-child{text-align:left;}
.oz2-table td.sym{color:var(--text);font-weight:600;}
.oz2-badge{font-size:8px;padding:1px 4px;border-radius:4px;background:var(--layer3);margin-left:5px;}
.oz2-empty{font-family:'JetBrains Mono',monospace;font-size:11px;color:var(--muted);padding:14px 0;font-style:italic;}
.oz2-wire{display:flex;flex-direction:column;gap:2px;}
.oz2-wire-row{display:flex;gap:10px;align-items:center;font-family:'JetBrains Mono',monospace;font-size:11px;padding:7px 0;border-bottom:1px solid var(--divider);}
.oz2-wire-t{color:var(--muted);min-width:44px;}
.oz2-wire-a{font-size:8.5px;font-weight:600;border:1px solid;padding:1px 6px;border-radius:5px;min-width:50px;text-align:center;}
.oz2-wire-d{color:var(--muted);flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.oz2-wire-k{font-size:9px;font-weight:700;flex:none;}
.oz2-foot{max-width:1400px;margin:16px auto 0;font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--muted);text-align:center;}
.oz2-click{cursor:pointer;transition:background .15s,border-color .15s;}
.oz2-click:hover{background:var(--hover);}
tr.oz2-click:hover td{background:var(--hover);}
.oz2-hint{font-family:'JetBrains Mono',monospace;font-size:7.5px;color:var(--muted);border:1px solid var(--border);padding:1px 5px;border-radius:4px;margin-left:6px;letter-spacing:.5px;}
.pc2-backdrop{position:fixed;inset:0;background:rgba(0,0,0,0.55);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:18px;z-index:200;}
.pc2-card{--hue:var(--purple);width:100%;max-width:440px;max-height:86vh;overflow-y:auto;background:var(--surface);border:1px solid var(--border);border-top:3px solid var(--hue);border-radius:16px;box-shadow:0 30px 80px rgba(0,0,0,0.35);}
.pc2-head{display:flex;justify-content:space-between;align-items:flex-start;padding:16px 16px 10px;position:sticky;top:0;background:var(--surface);}
.pc2-title{font-size:17px;font-weight:700;}
.pc2-sub{font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--muted);margin-top:3px;}
.pc2-close{background:var(--layer2);border:1px solid var(--border);color:var(--text);width:28px;height:28px;border-radius:8px;font-size:17px;line-height:1;cursor:pointer;flex:none;}
.pc2-close:hover{background:var(--layer3);}
.pc2-body{padding:2px 16px 16px;}
.pc2-read{font-size:12px;line-height:1.55;color:var(--muted);margin:10px 0 4px;background:var(--layer1);border:1px solid var(--border);border-radius:9px;padding:11px 12px;}
.pc2-sub-t{font-family:'JetBrains Mono',monospace;font-size:8.5px;color:var(--muted);letter-spacing:1.5px;margin:14px 0 6px;}
.pc2-vec{padding:7px 0;border-bottom:1px solid var(--divider);}
.pc2-vec-head{display:flex;justify-content:space-between;font-family:'JetBrains Mono',monospace;font-size:11px;}
.pc2-vec-w{color:var(--muted);font-weight:400;}
.pc2-vec-bar{height:4px;background:var(--layer3);border-radius:99px;margin:5px 0;overflow:hidden;}
.pc2-vec-fill{height:100%;border-radius:99px;}
.pc2-vec-m{font-size:10.5px;color:var(--muted);line-height:1.4;}
@media(max-width:640px){.oz2-hero-v{font-size:28px;}.oz2-head{flex-wrap:wrap;gap:10px;}}
`;
